import { PaginaProvider, usePagina } from "@base/hooks/usePagina/usePagina";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Entregable as ModelClass } from "@/models/Entregable.model";
import { DefaultContainer } from "@base/components/layout/containers/DefaultContainer";
import {
  Avatar,
  Card,
  Col,
  DatePicker,
  Divider,
  Empty,
  Grid,
  Pagination,
  Row,
  Spin,
} from "antd";
import { CrudHeader } from "@base/components/layout/crud/CrudHeader";
import { TablaQuery } from "@base/components/tables/TablaQuery";
import useModelColumns from "@base/hooks/useModelColumns/useModelColumns";
import ActionsButton from "@base/components/buttons/ActionsButton";
import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import dayjs from "dayjs";
import BuscadorAutomatico from "@base/components/form/BuscadorAutomatico";
import IconoCfdi from "@/components/IconoCfdi";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Entrega } from "@/models/Entrega.model";
import ProgresoAvatar from "./-components/ProgresoAvatar";
import { useQuery } from "@tanstack/react-query";
import useHttp from "@base/hooks/useHttp/useHttp";

export const Route = createFileRoute("/(auth)/entregas/")({
  component: () => (
    <PaginaProvider titulo={`Listado de ${ModelClass.CLASS_NAME}s`}>
      <RouteComponent />
    </PaginaProvider>
  ),
});

const CARDS_POR_PAGINA = 10;

function RouteComponent() {
  const { requestParams, setRequestParams } = usePagina();
  const navigate = useNavigate();
  const http = useHttp();

  const screens = Grid.useBreakpoint();
  const isDesktop = !!screens.lg;

  const [paginaCards, setPaginaCards] = useState(1);

  const columns = useModelColumns({
    columns: ModelClass.COLUMNS,
    actionsButtonRender: () => <ActionsButton />,
  });

  const entregablesParams = {
    ordenar: "creado-desc",
    ...requestParams,
    expand: "empresa,totalAdjuntos,totalEntregables,factura,entregas,proyecto",
  };

  const qCards = useQuery({
    queryKey: [
      ModelClass.ENDPOINTS.DEFAULT,
      "cards",
      entregablesParams,
      paginaCards,
      http,
    ],
    queryFn: () =>
      http.get({
        endpoint: ModelClass.ENDPOINTS.DEFAULT,
        params: {
          ...entregablesParams,
          pagina: paginaCards,
          limite: CARDS_POR_PAGINA,
        },
      }),
    enabled: !isDesktop,
  });

  const entregablesCards: any[] = qCards.data?.resultado ?? [];
  const totalCards: number =
    qCards.data?.paginacion?.total ?? entregablesCards.length;

  const _columns = useMemo(() => {
    const cols = columns;

    cols.splice(2, 0, {
      title: "CFDI",
      dataIndex: "entregas",
      key: "entregas",
      width: "5%",
      render: (d: any) =>
        d?.some((e: Entrega) => e?.tipo === "FACTURA") ? (
          <IconoCfdi
            factura={d?.find((e: Entrega) => e?.tipo === "FACTURA")?.factura}
            width="100%"
          />
        ) : (
          <Icon icon={"lucide:folder-archive"} width={80} />
        ),
    });

    cols.push({
      title: "Descripción",
      dataIndex: "proyecto.descripcion",
      key: "proyecto.descripcion",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          <div className="max-w-xs truncate">
            {parse(`${r?.proyecto?.descripcion}`)}
          </div>
        </Link>
      ),
    });

    cols.push({
      title: "Fecha de Inicio",
      dataIndex: "proyecto.inicio",
      key: "proyecto.inicio",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {r?.proyecto?.inicio
            ? dayjs(r?.proyecto?.inicio).format("DD/MM/YYYY HH:mm")
            : "-"}
        </Link>
      ),
    });

    cols.push({
      title: "Fecha de Entrega",
      dataIndex: "proyecto.fin",
      key: "proyecto.fin",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {r?.proyecto?.fin
            ? dayjs(r?.proyecto?.fin).format("DD/MM/YYYY HH:mm a")
            : "-"}
        </Link>
      ),
    });

    cols.push({
      title: "Fecha Completado",
      dataIndex: "cierre",
      key: "cierre",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          {r?.cierre ? dayjs(r?.cierre).format("DD/MM/YYYY HH:mm a") : "-"}
        </Link>
      ),
    });

    cols.push({
      title: "Adjuntos",
      dataIndex: "totalAdjuntos",
      key: "totalAdjuntos",
      align: "center",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          <div className="flex flex-row justify-center">
            <Avatar size={"small"} className="bg-neutral-950">
              {r?.totalAdjuntos}
            </Avatar>
          </div>
        </Link>
      ),
    });

    cols.push({
      title: "Entregas",
      dataIndex: "totalEntregables",
      key: "totalEntregables",
      align: "center",
      render: (_: any, r: any) => (
        <Link to="/entregas/detalle" search={{ id: r?.id }}>
          <div className="flex flex-col gap-1">
            <div>
              <ProgresoAvatar
                completed={r?.totalEntregables?.pendientes ?? 0}
                total={r?.totalEntregables?.total ?? 0}
              />
            </div>
            {r.cierre && (
              <p className="text-xs text-neutral-500">
                {dayjs(r?.cierre).format("DD/MM/YYYY HH:mm a")}
              </p>
            )}
          </div>
        </Link>
      ),
    });

    return cols;
  }, [columns]);

  useEffect(() => {
    setPaginaCards(1);
  }, [requestParams?.inicio, requestParams?.fin, requestParams?.buscar]);

  return (
    <DefaultContainer className="h-full">
      <Row gutter={[24, 16]}>
        <Col span={24}>
          <CrudHeader
            titulo={"Mis Entregas"}
            agregarLabel={`Agregar ${ModelClass.CLASS_NAME}`}
          />
        </Col>
        <Col span={24}>
          <Card classNames={{ body: "p-4" }}>
            <Row gutter={[5, 5]}>
              <Col span={24} md={8}>
                <div className="flex flex-col w-full">
                  <p className="font-semibold">
                    Buscar
                    <BuscadorAutomatico
                      setRequestParams={setRequestParams}
                      responsive
                      hideClearButton
                    />
                  </p>
                </div>
              </Col>
              <Col span={24} md={6}>
                <div className="flex flex-col w-full">
                  <p className="font-semibold">Desde</p>
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    format={"DD/MM/YYYY"}
                    onChange={(v) => {
                      if (v) {
                        setRequestParams((prev) => ({
                          ...prev,
                          inicio: dayjs(v).format("YYYY-MM-DD"),
                        }));
                      } else {
                        setRequestParams((prev) => ({ ...prev, inicio: "" }));
                      }
                    }}
                  />
                </div>
              </Col>
              <Col span={24} md={6}>
                <div className="flex flex-col w-full">
                  <p className="font-semibold">Hasta</p>
                  <DatePicker
                    size="large"
                    style={{ width: "100%" }}
                    format={"DD/MM/YYYY"}
                    disabledDate={(current) => {
                      if (requestParams?.inicio) {
                        return (
                          current &&
                          current.isBefore(dayjs(requestParams.inicio), "day")
                        );
                      }
                      return false;
                    }}
                    onChange={(v) => {
                      if (v) {
                        setRequestParams((prev) => ({
                          ...prev,
                          fin: dayjs(v).format("YYYY-MM-DD"),
                        }));
                      } else {
                        setRequestParams((prev) => ({ ...prev, fin: "" }));
                      }
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col span={24}>
          {isDesktop ? (
            <TablaQuery
              columns={_columns}
              queryProps={{
                endpoint: ModelClass.ENDPOINTS.DEFAULT,
                extraParams: entregablesParams,
              }}
              setRequestParams={setRequestParams}
            />
          ) : (
            <Spin spinning={qCards.isLoading}>
              {entregablesCards.length === 0 && !qCards.isLoading ? (
                <Empty description="Sin entregas" />
              ) : (
                <>
                  <Row gutter={[10, 10]}>
                    {entregablesCards.map((r: any) => {
                      const factura = r?.entregas?.find(
                        (e: Entrega) => e?.tipo === "FACTURA",
                      )?.factura;

                      return (
                        <Col key={r?.id} xs={24} md={12}>
                          <Card
                            hoverable
                            size="small"
                            className="h-full"
                            onClick={() =>
                              navigate({
                                to: "/entregas/detalle",
                                search: { id: r?.id },
                              })
                            }
                            title={
                              <Link
                                to="/entregas/detalle"
                                search={{ id: r?.id }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {r?.folio || "-"}
                              </Link>
                            }
                            extra={
                              <ProgresoAvatar
                                completed={r?.totalEntregables?.pendientes ?? 0}
                                total={r?.totalEntregables?.total ?? 0}
                              />
                            }
                          >
                            <div className="flex flex-col gap-2 text-sm">
                              <div className="flex items-center gap-3">
                                <div className="w-12 shrink-0 flex justify-center">
                                  {factura ? (
                                    <IconoCfdi factura={factura} width="100%" />
                                  ) : (
                                    <Icon
                                      icon={"lucide:folder-archive"}
                                      width={40}
                                    />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <span className="block text-xs text-gray-500 font-medium">
                                    Descripción
                                  </span>
                                  <div className="line-clamp-2">
                                    {r?.proyecto?.descripcion
                                      ? parse(`${r.proyecto.descripcion}`)
                                      : "-"}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <span className="block text-xs text-gray-500 font-medium">
                                    Fecha de Inicio
                                  </span>
                                  <span>
                                    {r?.proyecto?.inicio
                                      ? dayjs(r.proyecto.inicio).format(
                                          "DD/MM/YYYY HH:mm",
                                        )
                                      : "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-xs text-gray-500 font-medium">
                                    Fecha de Entrega
                                  </span>
                                  <span>
                                    {r?.proyecto?.fin
                                      ? dayjs(r.proyecto.fin).format(
                                          "DD/MM/YYYY HH:mm a",
                                        )
                                      : "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="block text-xs text-gray-500 font-medium">
                                    Fecha Completado
                                  </span>
                                  <span>
                                    {r?.cierre
                                      ? dayjs(r.cierre).format(
                                          "DD/MM/YYYY HH:mm a",
                                        )
                                      : "-"}
                                  </span>
                                </div>
                              </div>

                              <Divider className="my-1" />

                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 font-medium">
                                  Adjuntos
                                </span>
                                <Avatar size="small" className="bg-neutral-950">
                                  {r?.totalAdjuntos ?? 0}
                                </Avatar>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>

                  <div className="flex justify-center mt-4">
                    <Pagination
                      current={paginaCards}
                      pageSize={CARDS_POR_PAGINA}
                      total={totalCards}
                      showSizeChanger={false}
                      onChange={(p) => setPaginaCards(p)}
                    />
                  </div>
                </>
              )}
            </Spin>
          )}
        </Col>
      </Row>
    </DefaultContainer>
  );
}
